import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { FileMinus } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  Button,
  MoneyCell,
  DateCell,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { siteConfig } from '@sighthost/config';
import { supabase } from '@/shared/lib/supabase';

/**
 * CreditNotesPage — RF-7 (2026-07-17): wired to the real credit_note query
 * (RLS-scoped to customer_id = auth.uid()). Read-only — credit notes are
 * issued by staff via the Record-refund flow (AD-16: refunds stay
 * ticket-driven; money moves manually).
 */

interface CreditNoteRow {
  id: string;
  number: string;
  related_invoice_id: string | null;
  amount: number | string;
  reason: string | null;
  issued_at: string;
}

export default function CreditNotesPage() {
  const [rows, setRows] = useState<CreditNoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('credit_note')
      .select('id, number, related_invoice_id, amount, reason, issued_at')
      .order('issued_at', { ascending: false })
      .then(({ data, error: qErr }) => {
        if (cancelled) return;
        if (qErr) setError(new Error(qErr.message));
        else setRows((data ?? []) as CreditNoteRow[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** FLOW — `Issued` is the event date. The customer's own set, unlimited. */
  const table = useTableFilters<CreditNoteRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Issued',
    dateOf: (r) => r.issued_at,
    searchText: (r) => [r.number, r.reason ?? ''].join(' '),
  });

  const columns = useMemo<UniversalTableColumn<CreditNoteRow>[]>(
    () => [
      {
        key: 'number',
        header: 'Credit note',
        sortValue: (r) => r.number,
        render: (r) => (
          <span className="text-sm font-semibold tabular-nums text-ink">{r.number}</span>
        ),
        width: 'w-44',
      },
      {
        key: 'issued',
        header: 'Issued',
        sortValue: (r) => r.issued_at,
        render: (r) => <DateCell value={r.issued_at} format="long" />,
      },
      {
        key: 'amount',
        header: 'Amount',
        sortValue: (r) => Number(r.amount),
        render: (r) => <MoneyCell value={Number(r.amount)} />,
        align: 'right',
        width: 'w-36',
      },
      {
        key: 'invoice',
        header: 'Related invoice',
        render: (r) =>
          r.related_invoice_id ? (
            <Link
              to={`/app/billing/invoices/${r.related_invoice_id}`}
              className="text-sm text-primary-ink hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              View invoice
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
        hideOnMobile: true,
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Credit notes"
      description="Credits issued against your account."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Credit notes' },
      ]}
    >
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Issued"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search number or reason"
            className="ml-auto w-full max-w-xs"
          />
        </div>
      )}

      <UniversalTable<CreditNoteRow>
        data={table.rows}
        columns={columns}
        getRowKey={(r) => r.id}
        isLoading={loading}
        error={error}
        caption={loading ? undefined : table.caption}
        empty={{
          icon: FileMinus,
          title: rows.length > 0 ? 'Nothing in this period' : 'No credit notes yet',
          description: `If we issue a credit note (e.g. a partial refund or goodwill adjustment) it'll appear here. To request a refund, use the link on the paid invoice or email ${siteConfig.supportEmail}.`,
        }}
      />

      <Card interactive={false}>
        <div className="px-6 py-3 text-center">
          <Button asChild size="sm" variant="outline">
            <Link to="/app/support/new?dept=billing">Open a billing ticket</Link>
          </Button>
        </div>
      </Card>
    </UniversalPage>
  );
}
